import cn from 'classnames';
import { ChangeEvent, useState } from 'react';
import {
  FieldErrors,
  FieldNamesMarkedBoolean,
  FieldValues,
  UseFormRegister,
} from 'react-hook-form';
import ReactInputMask from 'react-input-mask';

import { Check } from '@/components/Icons';
import { DataFromFormInputs } from '@/types';
import { valSchema } from '@/utils/valSchema';

import s from './Input.module.scss';

type BaseInputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> &
  InputVariant;

type ValidationType = 'name' | 'phone' | 'email' | 'default';

interface InputBase {
  label?: string;
  name: string;
  errors: FieldErrors<DataFromFormInputs>;
  placeholder?: string;
  dirtyFields: FieldNamesMarkedBoolean<FieldValues>;
  register: UseFormRegister<FieldValues>;
  disabled?: boolean;
  required: boolean;
  validationType: ValidationType;
  className?: string;
  inputClassName?: string;
  errorClassName?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
  inputType?: 'inn' | 'phone';
}

interface PhoneInput extends InputBase {
  inputType: 'phone';
}

interface TextInput extends InputBase {
  inputType?: undefined;
}

type InputVariant = PhoneInput | TextInput;

const getSchema = (
  validationType: ValidationType,
  required: boolean,
  disabled?: boolean,
) => {
  const schema = valSchema[validationType];
  return {
    ...schema,
    required: !disabled && required ? schema?.required : false,
  };
};

const Input = ({
  label,
  name,
  errors,
  placeholder,
  dirtyFields,
  register,
  required,
  disabled,
  inputType,
  validationType,
  className = '',
  inputClassName = '',
  errorClassName = '',
  onChange,
  onBlur,
  ...rest
}: BaseInputProps) => {
  const {
    onChange: onChangeCustom,
    onBlur: onBlurCustom,
    ...restOptions
  } = register(name);
  const [phoneValue, setPhoneValue] = useState('+7 (___) ___-__-__');

  const onChangeInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeCustom(event);
    onChange && onChange(event);
  };

  const onBlurInputHandler = (event: ChangeEvent<HTMLInputElement>) => {
    onBlurCustom(event);
    onBlur && onBlur(event);
  };

  const phoneRegExp = /^(\s*)?(\+)?([- ():=+]?\d[- ():=+]?){11,14}(\s*)?$/;

  const errorCondition = () => {
    if (name in errors) {
      const message = errors[name]?.message;

      if (message?.length && message?.length > 0) {
        return message;
      } else {
        return 'Обязательное поле';
      }
    }

    return null;
  };

  return (
    <div
      className={cn(s.root, className, errorClassName, {
        [s.error]: errors && errors[name],
        [s.valid]: dirtyFields && dirtyFields[name] && errors && !errors[name],
      })}
    >
      <div className={s.inner}>
        <div className={s.wrapper}>
          {inputType === 'phone' && (
            <ReactInputMask
              className={cn(s.input, s.inputPhone, {
                [s.phoneCorrect]:
                  phoneValue !== '+7 (___) ___-__-__' &&
                  phoneRegExp.test(phoneValue),
              })}
              alwaysShowMask
              defaultValue="+7 (___) ___-__-__"
              mask="+7 (999) 999-99-99"
              {...register(name, getSchema(validationType, required, disabled))}
              onChange={(event) => {
                onChangeInputHandler(event);
                setPhoneValue(event.target.value);
              }}
              onBlur={(event) => {
                onBlurInputHandler(event);
              }}
            />
          )}
          {!inputType && (
            <input
              className={cn(s.input, inputClassName)}
              id={name}
              placeholder={placeholder}
              {...register(name, getSchema(validationType, required, disabled))}
              disabled={disabled}
              {...rest}
              {...restOptions}
              onChange={(event) => onChangeInputHandler(event)}
              onBlur={(event) => onBlurInputHandler(event)}
            />
          )}
          <label className={s.name} htmlFor={name}>
            {label}
          </label>
          {required && <Check className={s.checkIcon} />}
        </div>
        {errors && errors[name] && (
          <span className={s.errorText}>{errorCondition()}</span>
        )}
      </div>
    </div>
  );
};

export { Input };
